import { CreateTeamForm, Keybind } from '@sparklayer-io/dashboard';
import type { Env } from '@sparklayer-io/spark-dash-core/context/env-context';
import { shopifyStoreHandle } from '@sparklayer-io/spark-dash-core/lib/platform-links';
import { useAutoFocus } from '@sparklayer-io/spark-dash-ui/hooks/use-auto-focus';
import { useDebouncedState } from '@sparklayer-io/spark-dash-ui/hooks/use-debounced-state';
import { useEventListener } from '@sparklayer-io/spark-dash-ui/hooks/use-event-listener';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { t } from 'i18next';
import { ChevronsUpDownIcon, GlobeIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiHandler } from '@/lib/handlers/api';
import { useDashboardHandlers } from '@/lib/handlers/use-dashboard-handlers';
import type { HostAvailableTeam, HostUser } from '@/lib/session';
import { I18N_SITE_SWITCHER } from './constants';
import { hasPermission, type RuntimeNavigate } from './helpers';

type SiteSwitcherProps = {
  env: Env;
  user: HostUser;
  siteId: string;
};

/** The team switcher at the top of the sidebar - trigger, search/list dropdown, "Create Team"
 * modal, and the `Cmd+K` shortcut that opens it. Fully self-contained: own open/search state,
 * outside-click handling, and the advanced (staff) team search query. */
export function SiteSwitcher({ env, user, siteId }: SiteSwitcherProps) {
  const router = useRouter();
  const navigateTo = router.navigate as unknown as RuntimeNavigate;

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [useAdvancedSearch, setUseAdvancedSearch] = useState<boolean>(false);
  const [showCreateTeamForm, setShowCreateTeamForm] = useState<boolean>(false);

  const debouncedSearch = useDebouncedState(search, 250);
  const isSparkStaff = !!user.user.is_spark_staff;

  // Staff should always use the advanced search - set once the user is known.
  useEffect(() => {
    setUseAdvancedSearch(isSparkStaff);
  }, [isSparkStaff]);

  const advancedTeamsQuery = useQuery({
    queryKey: ['host', 'available-teams-advanced', debouncedSearch],
    queryFn: () =>
      apiHandler.fetch<HostAvailableTeam[]>({
        method: 'GET',
        path: `/search-available-teams-advanced?search=${encodeURIComponent(debouncedSearch)}`,
      }),
    enabled: useAdvancedSearch && debouncedSearch !== '',
  });

  const availableTeams = user.availableTeams ?? [];
  const showTeamSearch = isSparkStaff || availableTeams.length > 10;

  const isSearchingTeams =
    useAdvancedSearch &&
    search !== '' &&
    (search !== debouncedSearch || advancedTeamsQuery.isFetching);

  const teams = useMemo(() => {
    if (useAdvancedSearch) {
      return debouncedSearch === '' ? [] : (advancedTeamsQuery.data ?? []);
    }
    const query = search.toLowerCase();
    return availableTeams
      .filter((team) => team.name.toLowerCase().includes(query))
      .sort((a, b) => Number(b.is_partner) - Number(a.is_partner));
  }, [useAdvancedSearch, debouncedSearch, advancedTeamsQuery.data, search, availableTeams]);

  const handlers = useDashboardHandlers({ siteId, siteEnv: env.environment, env });

  useAutoFocus(searchInputRef, open && showTeamSearch);

  useEventListener(
    document,
    'click',
    (event) => {
      const target = event.target as Node | null;
      if (!(target && containerRef.current?.contains(target))) {
        setOpen(false);
      }
    },
    open,
  );

  useEventListener(document, 'keydown', (event) => {
    if (!event.metaKey || event.key.toLowerCase() !== 'k') return;
    if (!hasPermission(user, 'manageAccount')) return;
    event.preventDefault();
    setOpen((o) => !o);
  });

  function toggleOpen() {
    if (!hasPermission(user, 'manageAccount')) return;
    setOpen((o) => !o);
  }

  async function changeSite(teamId: string | number, newTeam = false) {
    await navigateTo({ to: `/switch-account/${teamId}${newTeam ? '?new-team' : ''}` });
    setOpen(false);
  }

  function handleTeamCreated(teamId: string) {
    setShowCreateTeamForm(false);
    void changeSite(teamId, true);
  }

  return (
    <>
      <div ref={containerRef} className="relative mt-1 mb-5 w-full px-4 text-left">
        <button
          type="button"
          className="group w-full cursor-pointer rounded-lg border border-spark-border-subtle px-1.5 py-1.5 text-left font-medium text-sm text-spark-text-primary hover:bg-spark-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-spark-accent-hover focus-visible:ring-offset-2 focus-visible:ring-offset-spark-surface-subtle disabled:cursor-not-allowed"
          aria-haspopup="true"
          aria-expanded={open}
          disabled={!hasPermission(user, 'manageAccount')}
          onClick={toggleOpen}
        >
          <span className="flex w-full items-center justify-between">
            <span className="flex min-w-0 items-center space-x-2">
              <span className="inline-block size-9 shrink-0 overflow-hidden rounded-lg">
                <img
                  src="https://www.sparklayer.io/assets/images/icons/favicon.png"
                  className="size-full rounded-lg"
                  alt=""
                />
              </span>
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate font-semibold text-sm text-spark-text-primary">
                  {env.team.name}
                </span>
                <span className="block truncate font-normal text-spark-text-secondary text-xs">
                  {env.store_id ? shopifyStoreHandle(env.store_id) : ''}
                </span>
              </span>
            </span>
            <ChevronsUpDownIcon className="size-4.5 shrink-0 stroke-[1.5] text-spark-text-tertiary group-hover:text-spark-text-secondary" />
          </span>
        </button>

        <div
          className={cn(
            'absolute inset-x-0 z-40 mx-4 mt-1 origin-top rounded-lg bg-spark-surface shadow-lg ring-1 ring-spark-border-subtle',
            !open && 'hidden',
          )}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
        >
          {showTeamSearch && (
            <div className="px-2 pt-2 pb-1">
              <label
                htmlFor="search"
                className="hidden font-medium text-sm text-spark-text-primary"
              >
                {t(`${I18N_SITE_SWITCHER}.search_label`)}
              </label>
              <div className="relative flex items-center">
                <input
                  ref={searchInputRef}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  type="text"
                  name="search"
                  className="block w-full rounded-lg border-spark-border-default pr-12 text-sm shadow-xs focus:border-spark-accent-hover focus:ring-spark-accent-hover"
                  placeholder={t(`${I18N_SITE_SWITCHER}.search_placeholder`)}
                  autoComplete="off"
                />
                <div className="absolute inset-e-0 inset-y-0 flex py-1.5 pr-1.5">
                  <Keybind modifiers={['WIN']} shortcutKey="K" />
                </div>
              </div>
            </div>
          )}
          <div className="max-h-80 overflow-y-auto">
            {useAdvancedSearch && isSearchingTeams ? (
              Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="px-3 py-1">
                  <div className="shimmer h-4 w-full rounded-full" />
                </div>
              ))
            ) : (
              <>
                {teams.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={cn(
                      'relative block w-full cursor-pointer overflow-hidden text-ellipsis px-3 py-1 text-left text-sm text-spark-text-primary hover:bg-spark-surface-hover',
                      item.is_partner &&
                        !item.deletion_scheduled_for &&
                        'bg-spark-primary-soft hover:bg-spark-primary-soft-hover',
                      item.deletion_scheduled_for &&
                        'bg-spark-danger-soft text-spark-danger-text hover:bg-spark-danger-soft-hover',
                    )}
                    role="menuitem"
                    onClick={() => void changeSite(item.id)}
                  >
                    {item.name}
                    {item.is_partner && (
                      <GlobeIcon className="absolute inset-e-2 top-1.5 size-4 text-spark-primary-accent" />
                    )}
                  </button>
                ))}
                {teams.length === 0 && search && !isSearchingTeams && (
                  <p className="px-3 py-3 text-sm text-spark-text-secondary">
                    {t(`${I18N_SITE_SWITCHER}.no_teams_found`)}
                  </p>
                )}
              </>
            )}
            <div className={cn(showTeamSearch ? 'px-2 pt-1 pb-2' : 'p-1')}>
              <button
                type="button"
                className="inline-flex w-full cursor-pointer items-center justify-center rounded-lg border border-spark-border-default bg-spark-surface px-4 py-2 font-medium text-sm text-spark-text-primary shadow-xs hover:bg-spark-surface-hover focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-spark-accent-hover focus-visible:ring-offset-2 focus-visible:ring-offset-spark-surface"
                role="menuitem"
                onClick={() => setShowCreateTeamForm(true)}
              >
                <span className="font-medium">{t(`${I18N_SITE_SWITCHER}.create_team`)}</span>
              </button>
            </div>
            {availableTeams.length > 10 && (
              <p className="rounded-b-lg bg-spark-info-soft px-4 py-3 text-spark-text-secondary text-xs">
                {t(`${I18N_SITE_SWITCHER}.advanced_search_prompt`)}{' '}
                <button
                  type="button"
                  className="cursor-pointer text-spark-accent hover:text-spark-accent-hover"
                  onClick={() => setUseAdvancedSearch((v) => !v)}
                >
                  {useAdvancedSearch
                    ? t(`${I18N_SITE_SWITCHER}.use_regular_search`)
                    : t(`${I18N_SITE_SWITCHER}.use_advanced_search`)}
                </button>
              </p>
            )}
          </div>
        </div>
      </div>

      <CreateTeamForm
        handlers={handlers}
        isOpen={showCreateTeamForm}
        onClose={() => setShowCreateTeamForm(false)}
        onTeamCreated={handleTeamCreated}
      />
    </>
  );
}
